import { MultiFlightPathCanvasXEyeTrackingCanvas as MultiFlightPathCanvasXEyeTrackingCanvasComponent } from "heat-next";
import { Vector3 } from "three";

import { useSessionDetailsContext } from "@/context/session-details/session-details";

import { ComponentWrapper } from "@/pages/shared/shared.styles";

import ContentLoader from "@/components/content-loader/content-loader";
import EmptyState from "@/components/empty-state/empty-state";
import { EmptyStateType } from "@/components/empty-state/empty-state.types";

interface MultiFlightPathCanvasXEyeTrackingCanvasProps {
    isInsideModal?: boolean;
    subDimensionsData?: any;
}

const MultiFlightPathCanvasXEyeTrackingCanvas = ({
    isInsideModal,
    subDimensionsData,
}: MultiFlightPathCanvasXEyeTrackingCanvasProps) => {
    const {
        gazeData,
        isFetchingGazeData,
        isErrorGazeData,
        multiFlightCircuitData,
        isFetchingMultiFlightCircuit,
        isErrorMultiFlightCircuit,
    } = useSessionDetailsContext();

    //EyeTrackingCanvas
    const eyeTrackingCanvas = isInsideModal
        ? subDimensionsData?.flat()?.filter((item: any) => item?.chartType === "EyeTracking")
        : gazeData;

    //MultiFlightPath
    const flightCircuit = isInsideModal
        ? subDimensionsData?.flat()?.filter((item: any) => item?.chartType === "MultiFlightPath")
        : multiFlightCircuitData;

    //Conversion function to turn the values array from gazeData
    // into the accepted format for the Eye Tracking component
    function convertToLineSegments(data: any) {
        const lines = [];

        for (let i = 0; i < data?.length - 1; i++) {
            const curr = data[i];
            const next = data[i + 1];

            const from = new Vector3(curr.x, curr.y, curr.z);
            const to = new Vector3(next.x, next.y, next.z);

            lines.push({
                from: { x: from.x, y: from.y },
                to: { x: to.x, y: to.y },
                captureDate: new Date(curr.timestamp).toISOString(),
            });
        }

        return lines;
    }

    //fetch flightPathData per Intercept
    const getFlightPathData = (flightPath: any) => {
        const flightData = {
            canvasHeight: 288,
            canvasWidth: 720,
            fitAllPathsToPlot: false,
            textLabel: "Aircraft Range",
            textNumber: "0",
            topDownView: true,
            pathsData: flightPath?.pathsData,
        };

        return flightData;
    };

    const getEyeTrackingData = (circuit: any) => {
        const eyeTrackingDataPerCircuit = eyeTrackingCanvas?.filter((entry: any) => {
            return entry?.title === circuit?.title;
        });

        const eyeTracking = {
            data: {
                canvas: {
                    width: 496,
                    height: 288,
                    canvasFillColor: "#383838",
                },
                images: [
                    {
                        imageSrc: "aviation/cockpit",
                        centerX: 70,
                        centerY: 16,
                        width: 355.1436,
                        height: 254.1132,
                        opacity: 1,
                    },
                ],
                linesLayer: {
                    lines: convertToLineSegments(eyeTrackingDataPerCircuit?.[0]?.values), //Transform gaze_data for EyeTracking Canvas
                    defaultColor: "#c56e38",
                    defaultThickness: 3,
                    hasGradient: true,
                },
                shapeProps: {
                    shapeName: "Circle",
                    threshold: 5,
                    baseRadius: 10,
                    maxRadius: 10,
                    growthFactor: 10,
                    color: "#c56e38",
                    hasGradient: true,
                },
            },
        };

        return eyeTracking;
    };

    //Prepare data for FlightPathXEyeTrackingCanvas component per circuit
    const flightPathXEyeTrackingCanvasData = flightCircuit?.map((circuit: any) => {
        return {
            title: circuit.title || "Flight Path",
            eyeTrackingCanvasData: getEyeTrackingData(circuit),
            flightPathCanvasData: getFlightPathData(circuit),
        };
    });

    const isGazeDataEmptyState =
        !isErrorGazeData && (!eyeTrackingCanvas || eyeTrackingCanvas?.values?.length === 0);

    const isFlightCircuitDataEmptyState =
        !isErrorMultiFlightCircuit &&
        (!flightPathXEyeTrackingCanvasData ||
            flightPathXEyeTrackingCanvasData?.pathData?.length === 0);

    const isEmptyState = () => {
        if (isInsideModal) {
            return subDimensionsData?.every((v: any) => v === undefined);
        } else if (isFlightCircuitDataEmptyState || isGazeDataEmptyState) {
            return true;
        }
    };

    if (isFetchingMultiFlightCircuit || isFetchingGazeData) {
        return <ContentLoader height="28.875rem" />;
    }

    if (
        (!isInsideModal && isErrorMultiFlightCircuit && !isFetchingMultiFlightCircuit) ||
        (!isInsideModal && isErrorGazeData && !isFetchingGazeData)
    ) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="error-state:title"
                    emptyStateType={EmptyStateType.Error}
                    minHeight="25.875rem"
                />
            </ComponentWrapper>
        );
    }

    if (isEmptyState()) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-details:eye-tracking:empty-state:title"
                    emptyStateType={EmptyStateType.Charts}
                    minHeight="25.875rem"
                />
            </ComponentWrapper>
        );
    }

    if (isFetchingMultiFlightCircuit || isFetchingGazeData) {
        return <ContentLoader height={"26.4375rem"} />;
    }

    return (
        <MultiFlightPathCanvasXEyeTrackingCanvasComponent
            dualPlaybackProps={{
                rowGap: "0.5rem",
                leftPercent: 66,
            }}
            isInsideModal={isInsideModal}
            data={flightPathXEyeTrackingCanvasData}
        />
    );
};

export default MultiFlightPathCanvasXEyeTrackingCanvas;
